/**
 * © 2025 Amazon Web Services, Inc. or its affiliates. All Rights Reserved.
 */
import { NgFor, NgIf } from "@angular/common";
import {
  Component,
  inject,
  Input,
  OnInit,
  AfterViewInit,
  output,
  CUSTOM_ELEMENTS_SCHEMA,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
} from "@angular/forms";
import { SubscriptionsService } from "../services/user/subscriptions.service";

@Component({
  selector: "app-parameter-table",
  standalone: true,
  imports: [NgFor, NgIf, ReactiveFormsModule],
  templateUrl: "./parameter-table.component.html",
  styleUrl: "./parameter-table.component.css",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ParameterTableComponent implements OnInit, AfterViewInit {
  @Input() dashboardId!: string;
  @Input() initialParameterValues?: any; // Add input for initial parameter values in edit mode
  @Input() delayInitialization: boolean = false; // Control when to initialize parameters
  @Input() requiredParameters: string[] = []; // List of required parameter names (case-insensitive)
  @Input() invalidParameters: string[] = []; // List of parameter names that have validation errors
  onParameterChange = output<{}>();
  parameterNames: string[] = [];
  parameterConfigs: any = { parameters_from_definition: [] }; // Initialize with empty array to avoid template errors
  column: any;
  subscriptionsServices: SubscriptionsService = inject(SubscriptionsService);
  parametersForm!: FormGroup;
  parameters: { [key: string]: any } = {};
  isLoading: boolean = true;
  existingValues: any = {}; // Store form values before reset
  private isInitialized: boolean = false;
  private shouldInitializeParameters: boolean = false;

  constructor(private parentFormDirective: FormGroupDirective) {}

  ngOnInit(): void {
    this.parametersForm = this.parentFormDirective.form;

    // Store current form values for edit mode without destroying anything
    this.existingValues = this.initialParameterValues || {};

    // Mark that we should initialize parameters after view init
    this.shouldInitializeParameters = true;
  }

  ngAfterViewInit(): void {
    // Use AfterViewInit to ensure all components are properly initialized
    if (this.shouldInitializeParameters) {
      if (this.delayInitialization) {
        // For edit mode, wait for the main form to be fully populated
        this.waitForFormStability().then(() => {
          this.initializeParameters();
        });
      } else {
        // For create mode, initialize immediately
        this.initializeParameters();
      }
    }
  }

  private async waitForFormStability(): Promise<void> {
    // Wait for form to have the expected number of controls (indicating main form is ready)
    const expectedMinimumControls = 9; // Main form controls

    return new Promise((resolve) => {
      const checkFormStability = () => {
        const currentControlCount = Object.keys(
          this.parametersForm.controls
        ).length;

        if (currentControlCount >= expectedMinimumControls) {
          resolve();
        } else {
          // Use requestAnimationFrame for better performance than setTimeout
          requestAnimationFrame(checkFormStability);
        }
      };

      checkFormStability();
    });
  }

  private initializeParameters(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    this.isLoading = true;

    this.subscriptionsServices.getParameterConfigs(this.dashboardId).subscribe({
      next: (response) => {
        // Update parameterConfigs without changing reference to avoid change detection errors
        this.parameterConfigs.parameters_from_definition =
          response.parameters_from_definition || [];

        // Override options for pAsOfDate to match backend expectations
        if (this.parameterConfigs?.parameters_from_definition) {
          this.parameterConfigs.parameters_from_definition.forEach(
            (param: any) => {
              if (
                param.StringParameterDeclaration &&
                param.StringParameterDeclaration.Name.toLowerCase() ===
                  "pasofdate"
              ) {
                param.options = [
                  "Current Date",
                  "First Day of Month",
                  "Last Day of Month",
                  "First Day of Quarter",
                  "Last Day of Quarter",
                  "First Day of Year",
                  "Last Day of Year",
                ];
              }
            }
          );
        }

        this.initialTable();

        // Re-apply any existing values immediately after form controls are created
        if (
          this.existingValues &&
          Object.keys(this.existingValues).length > 0
        ) {
          Object.keys(this.existingValues).forEach((key) => {
            if (this.parametersForm.contains(key)) {
              const currentValue = this.parametersForm.get(key)?.value;

              if (
                this.existingValues[key] !== null &&
                this.existingValues[key] !== undefined
              ) {
                this.parametersForm
                  .get(key)
                  ?.setValue(this.existingValues[key]);

                const newValue = this.parametersForm.get(key)?.value;
              } else {
              }
            } else {
            }
          });

          this.updateParameters();
        } else {
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error("Error fetching parameter configs:", error);
        this.isLoading = false;
      },
    });
  }

  initialTable() {
    if (
      this.parameterConfigs &&
      this.parameterConfigs.parameters_from_definition
    ) {
      this.parameterConfigs.parameters_from_definition.forEach(
        (element: {
          DateTimeParameterDeclaration: { Name: any };
          StringParameterDeclaration: {
            Name: any;
            DefaultValues: any;
            ParameterValueType: any;
          };
          IntegerParameterDeclaration: any;
          options: any;
        }) => {
          try {
            if (element.DateTimeParameterDeclaration) {
              const controlName = element.DateTimeParameterDeclaration.Name;
              if (!this.parametersForm.contains(controlName)) {
                this.parametersForm.addControl(
                  controlName,
                  new FormControl(Date())
                );
              }
              this.parameterNames.push(controlName);
            } else if (element.StringParameterDeclaration) {
              const controlName = element.StringParameterDeclaration.Name;
              if (!this.parametersForm.contains(controlName)) {
                if (
                  element.StringParameterDeclaration.ParameterValueType ===
                  "MULTI_VALUED"
                ) {
                  this.parametersForm.addControl(
                    controlName,
                    new FormControl([])
                  );
                } else {
                  // SINGLE_VALUED StringParameterDeclaration
                  this.parametersForm.addControl(
                    controlName,
                    new FormControl("")
                  );
                }
              }
              this.parameterNames.push(controlName);
            } else if (element.IntegerParameterDeclaration) {
              const controlName = element.IntegerParameterDeclaration.Name;
              if (!this.parametersForm.contains(controlName)) {
                if (
                  element.IntegerParameterDeclaration.ParameterValueType ===
                  "MULTI_VALUED"
                ) {
                  this.parametersForm.addControl(
                    controlName,
                    new FormControl([])
                  );
                } else {
                  this.parametersForm.addControl(
                    controlName,
                    new FormControl("")
                  );
                }
              }
              this.parameterNames.push(controlName);
            }
          } catch (error) {
            console.error(`Error adding control for element:`, element, error);
          }
        }
      );
    }
    this.updateParameters();
  }

  isAnySelected(controlName: string): boolean {
    const control = this.parametersForm.get(controlName);
    const value = control?.value;
    return Array.isArray(value) && value.length > 0;
  }

  isAllSelected(controlName: string): boolean {
    const control = this.parametersForm.get(controlName);
    const currentValue = control?.value || [];
    const allOptions = this.getAllOptionsForControl(controlName);
    
    // If we don't have options loaded yet or no current values, return false
    if (!Array.isArray(currentValue) || allOptions.length === 0) {
      return false;
    }
    
    // Filter out empty strings from current values
    const validCurrentValues = currentValue.filter(value => value !== null && value !== undefined);
    
    // If no valid values are selected, return false
    if (validCurrentValues.length === 0) {
      return false;
    }
    
    // Check if all available options are selected
    return allOptions.length > 0 && allOptions.every(option => validCurrentValues.includes(option));
  }

  getAllOptionsForControl(controlName: string): string[] {
    if (!this.parameterConfigs?.parameters_from_definition) {
      return [];
    }

    for (const param of this.parameterConfigs.parameters_from_definition) {
      let paramName = '';
      
      if (param.StringParameterDeclaration?.Name === controlName) {
        paramName = param.StringParameterDeclaration.Name;
        
        // Check if this parameter has predefined options
        if (param.options && Array.isArray(param.options)) {
          return param.options;
        }
        
        // Check if it has default values that represent options
        if (param.StringParameterDeclaration.DefaultValues && Array.isArray(param.StringParameterDeclaration.DefaultValues)) {
          return param.StringParameterDeclaration.DefaultValues;
        }
      } else if (param.IntegerParameterDeclaration?.Name === controlName) {
        paramName = param.IntegerParameterDeclaration.Name;
        
        // Check if this parameter has predefined options
        if (param.options && Array.isArray(param.options)) {
          return param.options.map((option: any) => option.toString());
        }
        
        // Check if it has default values that represent options
        if (param.IntegerParameterDeclaration.DefaultValues && Array.isArray(param.IntegerParameterDeclaration.DefaultValues)) {
          return param.IntegerParameterDeclaration.DefaultValues.map((value: any) => value.toString());
        }
      }
    }
    
    return [];
  }

  toggleSelectAll(controlName: string) {
    const control = this.parametersForm.get(controlName);
    if (!control) {
      return;
    }

    const allOptions = this.getAllOptionsForControl(controlName);
    
    if (allOptions.length === 0) {
      console.warn(`No options found for parameter: ${controlName}`);
      return;
    }

    // If all options are selected, clear selection; otherwise select all
    if (this.isAllSelected(controlName)) {
      control.setValue([]);
    } else {
      control.setValue([...allOptions]);
    }
    
    this.updateParameters();
  }

  updateParameters(): void {
    this.parameterNames.forEach((parameterName) => {
      const control = this.parametersForm.get(parameterName);
      if (control) {
        const value = control.value;

        this.parameters[parameterName] = value;
      } else {
      }
    });

    this.onParameterChange.emit(this.parameters);
  }

  isParameterRequired(parameterName: string): boolean {
    if (!this.requiredParameters || this.requiredParameters.length === 0) {
      return false;
    }
    const normalizedParamName = parameterName.toLowerCase();
    return this.requiredParameters.some(reqParam => reqParam.toLowerCase() === normalizedParamName);
  }

  hasError(parameterName: string): boolean {
    if (!this.invalidParameters || this.invalidParameters.length === 0) {
      return false;
    }
    const normalizedParamName = parameterName.toLowerCase();
    return this.invalidParameters.some(invalidParam => invalidParam.toLowerCase() === normalizedParamName);
  }

  getErrorMessage(parameterName: string): string {
    return this.hasError(parameterName) ? 'Please select a value' : '';
  }
}
